import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { BadgeService } from '../lib/badges/badgeService';
import { BadgeDefinitionsResponse, UserBadgesResponse, BadgeResponse } from '../lib/badges/types';
import type { EnhancedBadgeCriteria, BadgePreview } from '../types/badge-criteria';

// Query keys
export const badgeQueryKeys = {
  all: ['badges'] as const,
  definitions: () => [...badgeQueryKeys.all, 'definitions'] as const,
  userBadges: (userId: string) => [...badgeQueryKeys.all, 'user', userId] as const,
  plantBadges: (plantId: string) => [...badgeQueryKeys.all, 'plant', plantId] as const,
  preview: (criteria: EnhancedBadgeCriteria) =>
    [...badgeQueryKeys.all, 'preview', criteria] as const,
};

// Badge definitions query
export function useBadgeDefinitions() {
  return useQuery<BadgeDefinitionsResponse>({
    queryKey: badgeQueryKeys.definitions(),
    queryFn: BadgeService.getBadgeDefinitions,
    staleTime: 1000 * 60 * 60, // 1 hour - badge definitions don't change often
    gcTime: 1000 * 60 * 60 * 24, // 24 hours
  });
}

// User badges query
export function useUserBadges(
  userId: string,
  enabled = true,
  options: { refetchInterval?: number; staleTime?: number } = {}
) {
  return useQuery<UserBadgesResponse>({
    queryKey: badgeQueryKeys.userBadges(userId),
    queryFn: () => BadgeService.getUserBadges(userId),
    enabled: enabled && !!userId,
    staleTime: options.staleTime ?? 1000 * 60 * 5, // 5 minutes default
    gcTime: 1000 * 60 * 10, // 10 minutes
    refetchInterval: options.refetchInterval,
    refetchIntervalInBackground: false,
  });
}

// Plant badges query
export function usePlantBadges(plantId: string, enabled = true) {
  return useQuery<BadgeResponse[]>({
    queryKey: badgeQueryKeys.plantBadges(plantId),
    queryFn: () => BadgeService.getPlantBadges(plantId),
    enabled: enabled && !!plantId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
  });
}

// Badge evaluation mutation
export function useBadgeEvaluation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => BadgeService.evaluateUserBadges(userId),
    onSuccess: (_, userId) => {
      // Invalidate user badges to refetch after evaluation
      queryClient.invalidateQueries({
        queryKey: badgeQueryKeys.userBadges(userId),
      });

      // Also invalidate all plant badges for this user's plants
      queryClient.invalidateQueries({
        queryKey: ['badges', 'plant'],
      });
    },
  });
}

// Hook to get enriched badges with local registry data
export function useEnrichedPlantBadges(plantId: string, enabled = true) {
  const { data: badgeResponses, ...query } = usePlantBadges(plantId, enabled);

  const enrichedBadges = badgeResponses ? BadgeService.enrichBadgeResponses(badgeResponses) : [];

  return {
    ...query,
    data: enrichedBadges,
  };
}

// Hook to get enriched user badges
export function useEnrichedUserBadges(userId: string, enabled = true) {
  const { data: userBadgesResponse, ...query } = useUserBadges(userId, enabled);

  const enrichedBadges = userBadgesResponse?.badges
    ? BadgeService.enrichBadgeResponses(userBadgesResponse.badges)
    : [];

  const badgeStats = BadgeService.getBadgeStats(enrichedBadges);

  return {
    ...query,
    data: {
      badges: enrichedBadges,
      stats: {
        ...badgeStats,
        // Include original server stats if available
        ...(userBadgesResponse?.stats || {}),
      },
    },
  };
}

// Admin hooks
export function useAdminBadgeDefinitions() {
  return useQuery<BadgeDefinitionsResponse>({
    queryKey: [...badgeQueryKeys.all, 'admin', 'definitions'],
    queryFn: BadgeService.getAdminBadgeDefinitions,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
  });
}

export function useCreateBadgeDefinition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (definition: Parameters<typeof BadgeService.createBadgeDefinition>[0]) =>
      BadgeService.createBadgeDefinition(definition),
    onSuccess: () => {
      // Invalidate both admin and public badge definitions
      queryClient.invalidateQueries({
        queryKey: badgeQueryKeys.definitions(),
      });
      queryClient.invalidateQueries({
        queryKey: [...badgeQueryKeys.all, 'admin', 'definitions'],
      });
    },
  });
}

export function useUpdateBadgeDefinition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      updates,
    }: {
      id: string;
      updates: Parameters<typeof BadgeService.updateBadgeDefinition>[1];
    }) => BadgeService.updateBadgeDefinition(id, updates),
    onSuccess: () => {
      // Invalidate both admin and public badge definitions
      queryClient.invalidateQueries({
        queryKey: badgeQueryKeys.definitions(),
      });
      queryClient.invalidateQueries({
        queryKey: [...badgeQueryKeys.all, 'admin', 'definitions'],
      });
    },
  });
}

export function useDeleteBadgeDefinition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => BadgeService.deleteBadgeDefinition(id),
    onSuccess: () => {
      // Invalidate both admin and public badge definitions
      queryClient.invalidateQueries({
        queryKey: badgeQueryKeys.definitions(),
      });
      queryClient.invalidateQueries({
        queryKey: [...badgeQueryKeys.all, 'admin', 'definitions'],
      });
    },
  });
}

// Badge template hooks
export function useBadgeTemplates() {
  return useQuery({
    queryKey: [...badgeQueryKeys.all, 'templates'],
    queryFn: () => BadgeService.getBadgeTemplates(),
    staleTime: 1000 * 60 * 10, // 10 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
    retry: (failureCount, error: { response?: { status?: number } }) => {
      // Don't retry on authentication errors
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        return false;
      }
      return failureCount < 2;
    },
  });
}

export function useCreateBadgeTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (template: Parameters<typeof BadgeService.createBadgeTemplate>[0]) =>
      BadgeService.createBadgeTemplate(template),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...badgeQueryKeys.all, 'templates'],
      });
    },
  });
}

export function useUpdateBadgeTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      updates,
    }: {
      id: string;
      updates: Parameters<typeof BadgeService.updateBadgeTemplate>[1];
    }) => BadgeService.updateBadgeTemplate(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...badgeQueryKeys.all, 'templates'],
      });
    },
  });
}

export function useDeleteBadgeTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => BadgeService.deleteBadgeTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...badgeQueryKeys.all, 'templates'],
      });
    },
  });
}

// Badge preview hook for real-time impact analysis
export function useBadgePreview(criteria: EnhancedBadgeCriteria, enabled = true) {
  return useQuery<BadgePreview>({
    queryKey: badgeQueryKeys.preview(criteria),
    queryFn: () => BadgeService.previewBadgeCriteria(criteria),
    enabled: enabled && criteria.rules.length > 0,
    staleTime: 1000 * 30, // 30 seconds - preview should be relatively fresh
    gcTime: 1000 * 60 * 5, // 5 minutes
    retry: (failureCount, error) => {
      // Don't retry on client errors
      const axiosError = error as { response?: { status?: number } };
      if (
        axiosError?.response?.status &&
        axiosError.response.status >= 400 &&
        axiosError.response.status < 500
      ) {
        return false;
      }
      return failureCount < 2;
    },
    refetchOnWindowFocus: false, // Don't refetch on window focus for performance
  });
}
